import type { FilledContentRelationshipField } from '@prismicio/client';

import { PVSProvider } from '@/models/customers/pvs-provider.ts';
import { EnrichedCustomer, EnrichedDCConfig, EnrichedLocation } from '@/models/enriched-prismic-documents.ts';
import { ConfigDcInstanceDocument, CustomerConfigDocument, HubDocument } from '@/models/prismicTypes.ts';
import { RoomType } from '@/models/room.ts';

import { colorHash } from '@/utils/color-utils.ts';
import { isCorrectWhiteLabelDomain, isEternoDomain, isLocalEnvironment } from '@/utils/env-utils.ts';
import { formatCustomerPrinters, getTextFromRichTextField } from '@/utils/prismic-utils.ts';

export const filterCustomerConfigsByDomain = (customer_configs: CustomerConfigDocument[]): CustomerConfigDocument[] => {
    if (isLocalEnvironment()) {
        return customer_configs;
    }

    if (isEternoDomain()) {
        return customer_configs.filter((entry: CustomerConfigDocument) => entry.data.is_eterno_customer);
    }

    return customer_configs.filter(
        (entry: CustomerConfigDocument) =>
            !entry.data.is_eterno_customer && entry.data.domain && isCorrectWhiteLabelDomain(entry.data.domain)
    );
};

export const filterCustomersForUser = (
    customers: EnrichedCustomer[],
    customer_id_set: Set<string>,
    instance_id_set: Set<string>,
    groups_set: Set<string>
): EnrichedCustomer[] => {
    const filteredCustomers: EnrichedCustomer[] = [];
    for (const customer of customers) {
        if (!customer_id_set.has(customer.customer_id)) continue;

        const filteredLocations: EnrichedLocation[] = customer.locations
            .map((location) => ({
                ...location,
                configs: location.configs.filter((config) => instance_id_set.has(config.instance_id)),
            }))
            .filter((location) => location.configs.length > 0 && groups_set.has(location.group_key));

        if (filteredLocations.length === 0) {
            continue;
        }

        filteredCustomers.push({
            ...customer,
            locations: filteredLocations,
        });
    }

    return filteredCustomers;
};

export const buildEnrichedConfigArray = (config_documents: ConfigDcInstanceDocument['data'][]): EnrichedDCConfig[] => {
    const configs: EnrichedDCConfig[] = [];
    for (const configDocument of config_documents) {
        let pvsProvider: PVSProvider;
        if (isPVSProvider(configDocument.provider)) {
            pvsProvider = configDocument.provider;
        } else {
            pvsProvider = PVSProvider.DOC_CIRRUS;
        }

        configs.push({
            ...configDocument,
            instance_id: getTextFromRichTextField(configDocument.instance_id),
            pcs_printers: formatCustomerPrinters(configDocument.pcs_printers),
            rooms:
                configDocument.rooms.map((room) => ({
                    room_id: room.room_id ?? '',
                    room_name: room.room_name ?? '',
                    room_type: room.room_type as RoomType,
                })) ?? [],
            pvs_provider: pvsProvider,
        });
    }
    return configs;
};

export const populateInstanceConfigValues = (customers: EnrichedCustomer[]): EnrichedCustomer[] => {
    for (const customer of customers) {
        for (const location of customer.locations) {
            const configMap = new Map<string, EnrichedDCConfig>(
                location.configs.map((config) => [config.instance_id, config])
            );
            const configRecord: Record<string, EnrichedDCConfig> = Object.fromEntries(configMap);
            const instanceIds = location.configs.map((config) => config.instance_id);

            location.config_map = configMap;
            location.config_record = configRecord;
            location.instance_ids = instanceIds;
        }
    }
    return customers;
};

export const generateInstanceColors = (locations: EnrichedLocation[]): Record<string, string> => {
    const instanceColors: Record<string, string> = {};

    for (const location of locations) {
        for (const config of location.configs) {
            instanceColors[config.instance_id] = colorHash.hex(config.instance_id);
        }
    }

    return instanceColors;
};

export const isPVSProvider = (value: unknown): value is PVSProvider => {
    return typeof value === 'string' && Object.values(PVSProvider).includes(value as PVSProvider);
};

export const buildEnrichedCustomers = (
    customerConfigData: CustomerConfigDocument['data'][],
    locationData: Record<string, HubDocument['data']>,
    dcInstanceConfigData: Record<string, ConfigDcInstanceDocument['data'][]>
): { customers: EnrichedCustomer[]; instanceColors: Record<string, string> } => {
    const enrichedLocations: Record<string, EnrichedLocation> = {};
    for (const locationDocumentId of Object.keys(locationData)) {
        const location = locationData[locationDocumentId];
        const instanceConfigs = dcInstanceConfigData[locationDocumentId];

        if (!location || !instanceConfigs) continue;

        const enrichedConfigs: EnrichedDCConfig[] = buildEnrichedConfigArray(instanceConfigs);

        enrichedLocations[locationDocumentId] = {
            ...location,
            title: getTextFromRichTextField(location.title),
            key: getTextFromRichTextField(location.key),
            group_key: getTextFromRichTextField(location.group_key),
            configs: enrichedConfigs,
            config_map: new Map<string, EnrichedDCConfig>(),
            config_record: {},
            instance_ids: [],
        };
    }

    const customers: EnrichedCustomer[] = [];

    for (const customerConfig of customerConfigData) {
        const customerKey: string = getTextFromRichTextField(customerConfig.key);
        if (!customerKey) continue;

        const customerLocations: EnrichedLocation[] = [];
        for (const { location } of customerConfig.locations) {
            const documentId: string = (location as FilledContentRelationshipField<'hub'>)?.id;
            if (!documentId) continue;

            const locationObj: EnrichedLocation | undefined = enrichedLocations[documentId];
            if (!locationObj) continue;
            customerLocations.push(locationObj);
        }

        customers.push({
            ...customerConfig,
            key: customerKey,
            customer_id: getTextFromRichTextField(customerConfig.customer_id),
            customer_name: getTextFromRichTextField(customerConfig.customer_name),
            locations: customerLocations,
        });
    }

    const instanceColors: Record<string, string> = generateInstanceColors(Object.values(enrichedLocations));

    return { customers, instanceColors };
};

export interface FilteredCustomerGroup {
    customer: EnrichedCustomer;
    customerKey: string;
    customerName: string;
    locations: EnrichedLocation[];
}

export const filterCustomersByQuery = (
    customers: EnrichedCustomer[],
    query: string,
    limit: number
): FilteredCustomerGroup[] => {
    const formattedQuery = query.toLowerCase().trim();
    const results: FilteredCustomerGroup[] = [];

    for (const customer of customers) {
        if (results.length >= limit) break;

        if (!formattedQuery) {
            results.push({
                customer: customer,
                customerKey: customer.key,
                customerName: customer.customer_name,
                locations: customer.locations,
            });
            continue;
        }

        const isCustomerNameMatch = customer.customer_name.toLowerCase().trim().includes(formattedQuery);
        const filteredLocations = customer.locations.filter((location) =>
            location.title.toLowerCase().trim().includes(formattedQuery)
        );

        if (!isCustomerNameMatch && filteredLocations.length === 0) {
            continue;
        }

        results.push({
            customer: customer,
            customerKey: customer.key,
            customerName: customer.customer_name,
            locations: isCustomerNameMatch ? customer.locations : filteredLocations,
        });
    }

    return results;
};

export const resolveDefaultSelection = (
    userCustomers: EnrichedCustomer[],
    selectedCustomer: EnrichedCustomer | undefined,
    selectedLocation: EnrichedLocation | undefined
): { selectedCustomer: EnrichedCustomer; selectedLocation: EnrichedLocation } | undefined => {
    if (userCustomers.length === 0) return undefined;

    // Incomplete selection: at least one of customer/location is missing
    if (!selectedCustomer || !selectedLocation) {
        // If a location is already set, find the customer that owns it
        if (selectedLocation) {
            for (const candidate of userCustomers) {
                const canonicalLocation = candidate.locations.find((loc) => loc.key === selectedLocation.key);
                if (canonicalLocation) {
                    return { selectedCustomer: candidate, selectedLocation: canonicalLocation };
                }
            }
        }

        // Otherwise fall back to first customer with locations
        const defaultCustomer = userCustomers.find((c) => c.locations.length > 0);
        const defaultLocation = defaultCustomer?.locations[0];
        if (!defaultCustomer || !defaultLocation) return undefined;

        return {
            selectedCustomer: defaultCustomer,
            selectedLocation: defaultLocation,
        };
    }

    // Re-selection: find the matching customer/location in the updated list
    const selectedCustomerObj: EnrichedCustomer | undefined = userCustomers.find(
        (customer) => customer.customer_id === selectedCustomer.customer_id
    );
    const selectedLocationObj: EnrichedLocation | undefined = selectedCustomerObj?.locations.find(
        (location) => location.key === selectedLocation.key
    );

    if (!selectedCustomerObj || !selectedLocationObj) return undefined;

    return {
        selectedCustomer: selectedCustomerObj,
        selectedLocation: selectedLocationObj,
    };
};
